import moment from "moment";
import Review from "@/store/entities/review";
import Objective from "@/store/entities/objective";
import ActionCheckpoint from "@/store/entities/actionCheckpoint";
let _ = require("lodash");

class ReviewCalculator {
  calculateReview(review: Review, targetUser: Number, assessment: String, toDate: Date, exclusion: String) {
    let context = this;

    if (review && review.objectives) {
      let allUserTrackingResults = [];
      review.objectives.forEach(objective => {
        let target = objective.trackingTarget;
        if (isNaN(target) || target === null || target === 0) {
          target = 100; //
        }

        let checkpoints = context.filterCheckpoints(objective.checkpoints, targetUser, assessment, toDate, "none");
        objective.historicalCheckpoints = checkpoints.map(checkpoint => {
          return {
            id: checkpoint.id,
            objectiveName: objective.name,
            trackingDate: checkpoint.trackingDate,
            trackingDateString: moment(checkpoint.trackingDate).format("ddd, MMM D, YYYY"),
            result: checkpoint.value ? checkpoint.value / target : 0,
            value: checkpoint.value,
            targetUserId: checkpoint.targetUserId,
            targetUserName: checkpoint.targetUserName,
            trackerId: checkpoint.trackerId,
            trackerName: checkpoint.trackerName
          };
        });

        checkpoints = context.filterCheckpoints(objective.checkpoints, targetUser, assessment, new Date(), exclusion);
        objective.matchedCheckpoints = checkpoints.map(checkpoint => {
          return {
            id: checkpoint.id,
            objectiveName: objective.name,
            trackingDate: checkpoint.trackingDate,
            trackingDateString: moment(checkpoint.trackingDate).format("ddd, MMM D, YYYY"),
            result: checkpoint.value ? checkpoint.value / target : 0,
            value: checkpoint.value,
            targetUserId: checkpoint.targetUserId,
            targetUserName: checkpoint.targetUserName,
            trackerId: checkpoint.trackerId,
            trackerName: checkpoint.trackerName
          };
        });

        let score = NaN;
        let selfAverageResult = NaN;
        let otherAverageResult = NaN;
        let averageResult = NaN;
        let userTrackingResults = [];
        if (objective.matchedCheckpoints.length > 0) {
          selfAverageResult = _.meanBy(objective.matchedCheckpoints.filter(checkpoint => checkpoint.targetUserId === checkpoint.trackerId), "result");
          otherAverageResult = _.meanBy(objective.matchedCheckpoints.filter(checkpoint => checkpoint.targetUserId !== checkpoint.trackerId), "result");
          averageResult = _.meanBy(objective.matchedCheckpoints, "result");
          score = _.meanBy(objective.matchedCheckpoints, "result");
          userTrackingResults = _.chain(objective.matchedCheckpoints)
            .groupBy("targetUserId")
            .map((value, key) => {
              return {
                userId: key,
                selfResult: _.meanBy(value.filter(checkpoint => checkpoint.targetUserId === checkpoint.trackerId), "result"),
                otherResult: _.meanBy(value.filter(checkpoint => checkpoint.targetUserId !== checkpoint.trackerId), "result"),
                averageResult: _.meanBy(value, "result")
              };
            })
            .value();

          allUserTrackingResults = allUserTrackingResults.concat(userTrackingResults);
        }

        objective.trackingResult = score;
        objective.selfAverageResult = selfAverageResult;
        objective.otherAverageResult = otherAverageResult;
        objective.averageResult = averageResult;
        objective.userTrackingResults = userTrackingResults;

        checkpoints = context.filterCheckpoints(objective.checkpoints, 0, assessment, toDate, exclusion);
        objective.matchedTeamCheckpoints = context.mapCheckpoints(checkpoints, objective.name, objective.name, target);

        score = NaN;
        if (checkpoints.length > 0) {
          score = _.meanBy(objective.matchedTeamCheckpoints, "result");
        }

        objective.teamTrackingResult = score;
      });

      review.selfAverageResult = _.meanBy(review.objectives, "selfAverageResult");
      review.otherAverageResult = _.meanBy(review.objectives, "otherAverageResult");
      review.averageResult = _.meanBy(review.objectives, "averageResult");
      review.userTrackingResults = _.chain(allUserTrackingResults)
        .groupBy("userId")
        .map((value, key) => {
          return {
            userId: key,
            selfResult: _.meanBy(value, "selfResult"),
            otherResult: _.meanBy(value, "otherResult"),
            averageResult: _.meanBy(value, "averageResult")
          };
        })
        .value();
    }
  }

  mapCheckpoints(checkpoints: ActionCheckpoint[], objectiveName: string, actionName: string, target: number) {
    return checkpoints.map(checkpoint => {
      return {
        id: checkpoint.id,
        actionName: `${objectiveName} - ${actionName}`,
        trackingDate: checkpoint.trackingDate,
        trackingDateString: moment(checkpoint.trackingDate).format("ddd, MMM D, YYYY"),
        result: checkpoint.value ? checkpoint.value / target : 0,
        value: checkpoint.value,
        targetUserId: checkpoint.targetUserId,
        targetUserName: checkpoint.targetUserName,
        trackerId: checkpoint.trackerId,
        trackerName: checkpoint.trackerName
      };
    });
  }

  filterCheckpoints(checkpoints: [ActionCheckpoint], targetUser: Number, assessment: String, toDate: Date, exclusion: String) {
    if (!checkpoints) {
      return [];
    }

    //let endDate = exclusion === "lastCheckin" ? new Date() : toDate;
    let result = checkpoints.filter(cp => this.filterCheckpoint(cp, targetUser, assessment, toDate, exclusion));
    if (exclusion === "lastCheckin" && result.length > 1) {
      let max = _.maxBy(result, "trackingDate");
      result = [...result.filter(cp => cp.trackingDate === max.trackingDate)];
    }

    return result;
  }

  filterCheckpoint(cp: ActionCheckpoint, targetUser: Number, assessment: String, toDate: Date, exclusion: String) {
    let day = toDate || new Date();

    return (
      (new Date(cp.trackingDate) <= day || (cp.value !== null && !isNaN(cp.value))) &&
      (assessment === "self" ? cp.targetUserId === cp.trackerId : assessment === "other" ? cp.targetUserId !== cp.trackerId : true) &&
      (targetUser === 0 ? true : targetUser === cp.targetUserId) &&
      this.checkExclusion(cp, exclusion)
    );
  }

  checkExclusion(cp: ActionCheckpoint, exclusion: String) {
    if (exclusion === "overdue") {
      return cp.value !== null && !isNaN(cp.value);
    } else if (exclusion === "dueToday") {
      return new Date(cp.trackingDate).toDateString() === new Date().toDateString() ? cp.value !== null && !isNaN(cp.value) : true;
    } else if (exclusion === "lastCheckin") {
      return true;
    } else {
      return true;
    }
  }
}
export default new ReviewCalculator();
